import { WebcamPanorama } from "components/webcam-panorama"
import { WebcamArchive } from "components/webcam-archive"
import { BaseComponent, BElement } from "utils/base-component"
import snoweyeLogo from "components/resort-webcams/static/snoweye-logo.gif"
import "./webcam-popup.scss"

type Snapshot = {
  issued?: string
  image: string
  thumbnail: string
}

export type WebcamPopupData = {
  name: string
  attributionLabel?: string
  creditLabel?: string
  creditUrl?: string
  imageUrl?: string
  isSnoweye?: boolean
  specialUrl?: string
  specialType?: string
  aspectRatio?: string | number
  issuedLabel?: string
  snapshots?: Snapshot[]
  title?: string
  video?: string
}

const MAX_PANO_HEIGHT = 418

let activePopup: WebcamPopup | null = null

function preloadImage(imageUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()

    image.addEventListener(
      "load",
      () => {
        if (image.width + image.height >= 640) {
          resolve(image)
        } else {
          reject(new Error("Webcam image is too small"))
        }
      },
      { once: true },
    )
    image.addEventListener(
      "error",
      () => {
        reject(new Error("Failed to preload webcam image"))
      },
      { once: true },
    )

    image.src = imageUrl
  })
}

export class WebcamPopup extends BaseComponent {
  static cssClass = "webcam-popup"

  private archive?: WebcamArchive
  private initialParent!: HTMLElement | null
  private modalAttribution!: BElement | null
  private modalAttributionLabel!: BElement | null
  private modal!: BElement | null
  private modalArchive!: BElement | null
  private modalClose!: BElement | null
  private modalCredit!: BElement | null
  private modalCreditLink!: BElement | null
  private modalImage!: BElement | null
  private modalMedia!: BElement | null
  private modalOverlay!: BElement | null
  private modalPanorama!: BElement | null
  private modalSpecial!: BElement | null
  private panorama: WebcamPanorama | null = null
  private renderRequestId = 0
  private unmountObserver?: MutationObserver

  private get creditLink(): HTMLAnchorElement | null {
    return this.modalCreditLink?.element as HTMLAnchorElement | null
  }

  private get modalImageElement(): HTMLImageElement | null {
    return this.modalImage?.element as HTMLImageElement | null
  }

  init() {
    activePopup = this
    this.initialParent = this.root.parentElement
    this.ensureMarkup()

    this.modalAttribution = this.element("modal-attribution")
    this.modalAttributionLabel = this.element("modal-attribution-label")
    this.modal = this.element("modal")
    this.modalArchive = this.element("modal-archive")
    this.modalClose = this.element("modal-close")
    this.modalCredit = this.element("modal-credit")
    this.modalCreditLink = this.element("modal-credit-link")
    this.modalImage = this.element("modal-image")
    this.modalMedia = this.element("modal-media")
    this.modalOverlay = this.element("modal-overlay")
    this.modalPanorama = this.element("modal-panorama")
    this.modalSpecial = this.element("modal-special")

    this.archive = new WebcamArchive(null, {
      scrollIntoViewIfNeeded: () =>
        this.modalArchive?.element.scrollIntoView({ behavior: "smooth" }),
    })
    this.modalArchive?.element.appendChild(this.archive.root)

    this.modalOverlay?.element.addEventListener("click", (event) => {
      event.preventDefault()
      this.close()
    })
    this.modalClose?.element.addEventListener("click", (event) => {
      event.preventDefault()
      this.close()
    })

    this.unmountObserver = new MutationObserver(() => {
      if (!this.root.isConnected) {
        this.destroyPanorama()
      }
    })
    this.unmountObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
  }

  open(data: WebcamPopupData) {
    if (!this.modal || !this.modalAttribution || !this.modalAttributionLabel) return

    this.mountInVisibleRoot()
    this.modal.toggleModifier("hidden", false)
    this.renderMedia(data)

    if (this.modalCredit && this.modalCreditLink) {
      const hasCreditLink = Boolean(data.creditLabel && data.creditUrl)
      this.modalCredit.toggleModifier("hidden", !hasCreditLink)
      if (hasCreditLink && this.creditLink) {
        this.modalCreditLink.element.textContent = data.creditLabel || ""
        this.creditLink.href = data.creditUrl || "#"
      }
    }

    this.modalAttributionLabel.element.textContent = data.attributionLabel || ""
    this.modalAttribution.toggleModifier("hidden", !(data.isSnoweye && data.attributionLabel))

    document.addEventListener("keydown", this.handleEscape)
  }

  close() {
    this.renderRequestId += 1
    this.destroyPanorama()

    if (this.modalSpecial) {
      this.modalSpecial.element.innerHTML = ""
      this.modalSpecial.element.removeAttribute("style")
    }

    if (this.modalImage && this.modalImageElement) {
      this.modalImage.toggleModifier("hidden", false)
      this.modalImageElement.src = ""
      this.modalImageElement.alt = ""
    }

    this.archive?.reset()
    this.modalArchive?.toggleModifier("hidden", true)
    this.modalSpecial?.toggleModifier("hidden", true)
    this.modal?.toggleModifier("hidden", true)
    document.removeEventListener("keydown", this.handleEscape)
    this.restoreInitialParent()
  }

  destroy() {
    this.close()
    this.unmountObserver?.disconnect()
  }

  private renderMedia(data: WebcamPopupData) {
    if (
      !this.modalArchive ||
      !this.modalImage ||
      !this.modalSpecial ||
      !this.modalSpecial.element
    ) {
      return
    }

    this.renderRequestId += 1
    const requestId = this.renderRequestId
    this.destroyPanorama()

    if (data.snapshots?.length) {
      this.hideStandaloneMedia()
      this.modalArchive.toggleModifier("hidden", false)
      this.archive?.render(
        { snapshots: data.snapshots, video: data.video },
        {
          imageAlt: data.name,
          issuedLabel: data.issuedLabel,
          title: data.title || data.name,
        },
      )
      return
    }

    this.archive?.reset()
    this.modalArchive.toggleModifier("hidden", true)

    if (data.specialUrl && data.specialType !== "image") {
      const iframe = document.createElement("iframe")
      iframe.setAttribute("frameborder", "0")
      iframe.setAttribute("webkitallowfullscreen", "true")
      iframe.setAttribute("mozallowfullscreen", "true")
      iframe.setAttribute("allowfullscreen", "true")
      iframe.setAttribute("allow", "fullscreen")
      iframe.setAttribute("src", data.specialUrl)

      this.modalSpecial.element.innerHTML = ""
      this.modalSpecial.element.appendChild(iframe)
      this.modalSpecial.toggleModifier("hidden", false)

      if (data.aspectRatio) {
        this.modalSpecial.element.style.paddingTop = `${Number(data.aspectRatio) * 100}%`
      } else {
        this.modalSpecial.element.removeAttribute("style")
      }

      if (this.modalImageElement) {
        this.modalImage.toggleModifier("hidden", true)
        this.modalImageElement.src = ""
        this.modalImageElement.alt = ""
      }
      return
    }

    this.modalSpecial.element.innerHTML = ""
    this.modalSpecial.element.removeAttribute("style")
    this.modalSpecial.toggleModifier("hidden", true)

    const imageUrl =
      data.specialType === "image" && data.specialUrl ? data.specialUrl : data.imageUrl || ""

    if (imageUrl) {
      this.renderSnoweyeImage(imageUrl, data.name, requestId)
      return
    }

    this.renderRegularImage(imageUrl, data.name)
  }

  private hideStandaloneMedia() {
    this.destroyPanorama()
    this.modalSpecial?.element.replaceChildren()
    this.modalSpecial?.element.removeAttribute("style")
    this.modalSpecial?.toggleModifier("hidden", true)
    this.modalImage?.toggleModifier("hidden", true)
    if (this.modalImageElement) {
      this.modalImageElement.src = ""
      this.modalImageElement.alt = ""
    }
  }

  private async renderSnoweyeImage(imageUrl: string, alt: string, requestId: number) {
    this.hideStandaloneMedia()

    try {
      const image = await preloadImage(imageUrl)
      if (requestId !== this.renderRequestId) return

      if (this.imageIsPanoramic(image)) {
        this.renderPanorama(image)
      } else {
        this.renderRegularImage(imageUrl, alt)
      }
    } catch {
      if (requestId !== this.renderRequestId) return

      this.renderRegularImage(imageUrl, alt)
    }
  }

  private renderRegularImage(imageUrl: string, alt: string) {
    this.destroyPanorama()
    this.modalSpecial?.toggleModifier("hidden", true)

    if (this.modalImage && this.modalImageElement) {
      this.modalImage.toggleModifier("hidden", false)
      this.modalImageElement.src = imageUrl
      this.modalImageElement.alt = alt
    }
  }

  private imageIsPanoramic(image: HTMLImageElement) {
    const containerWidth = this.modalMedia?.element.offsetWidth || 0

    if (containerWidth === 0) return false

    return (
      image.width / image.height >= 2 &&
      (image.height <= MAX_PANO_HEIGHT ||
        (image.height > MAX_PANO_HEIGHT &&
          (image.width * MAX_PANO_HEIGHT) / image.height / containerWidth >= 1.5))
    )
  }

  private renderPanorama(image: HTMLImageElement) {
    if (!this.modalPanorama) return

    this.destroyPanorama()
    this.modalImage?.toggleModifier("hidden", true)
    this.modalPanorama.toggleModifier("hidden", false)
    this.panorama = new WebcamPanorama(this.modalPanorama.element, { image })
  }

  private destroyPanorama() {
    this.panorama?.destroy()
    this.panorama = null
    this.modalPanorama?.element.replaceChildren()
    this.modalPanorama?.toggleModifier("hidden", true)
  }

  private handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.close()
  }

  private mountInVisibleRoot() {
    const fullscreenRoot = document.fullscreenElement as HTMLElement | null
    if (fullscreenRoot && this.root.parentElement !== fullscreenRoot) {
      fullscreenRoot.appendChild(this.root)
      return
    }

    this.restoreInitialParent()
  }

  private restoreInitialParent() {
    if (this.initialParent && this.root.parentElement !== this.initialParent) {
      this.initialParent.appendChild(this.root)
    }
  }

  private ensureMarkup() {
    if (this.root.querySelector(this.elementClassSelector("modal"))) return

    const modal = this.div("modal", "hidden")
    modal.appendChild(this.div("modal-overlay"))

    const close = document.createElement("button")
    close.type = "button"
    close.classList.add(this.elementClassName("modal-close"))
    close.setAttribute("aria-label", "Close")
    close.textContent = "x"
    modal.appendChild(close)

    const content = this.div("modal-content")
    const media = this.div("modal-media")

    const image = document.createElement("img")
    image.classList.add(this.elementClassName("modal-image"))
    image.alt = ""
    media.appendChild(image)

    media.appendChild(this.div("modal-panorama", "hidden"))
    media.appendChild(this.div("modal-special", "hidden"))
    media.appendChild(this.div("modal-archive", "hidden"))
    content.appendChild(media)

    const body = this.div("modal-body")
    const credit = this.div("modal-credit", "hidden")
    credit.appendChild(document.createTextNode("Credit:"))
    credit.appendChild(this.link("modal-credit-link", "#", true))
    body.appendChild(credit)

    const attribution = this.div("modal-attribution", "hidden")
    const attributionLabel = document.createElement("span")
    attributionLabel.classList.add(this.elementClassName("modal-attribution-label"))
    attribution.appendChild(attributionLabel)

    const attributionLogo = document.createElement("img")
    attributionLogo.classList.add(this.elementClassName("modal-attribution-logo"))
    attributionLogo.src = snoweyeLogo
    attributionLogo.alt = "Snoweye Logo"
    attribution.appendChild(attributionLogo)

    body.appendChild(attribution)

    content.appendChild(body)
    modal.appendChild(content)
    this.root.appendChild(modal)
  }

  private div(name: string, mod = "") {
    const element = document.createElement("div")
    element.classList.add(this.elementClassName(name))
    if (mod) element.classList.add(this.elementModifierClassName(name, mod))
    return element
  }

  private link(name: string, href: string, nofollow = false) {
    const element = document.createElement("a")
    element.classList.add(this.elementClassName(name))
    element.href = href
    element.target = "_blank"
    element.rel = nofollow ? "nofollow noopener" : "noopener"
    return element
  }

  private elementClassSelector(name: string, mod = "") {
    return `.${this.elementModifierClassName(name, mod)}`
  }

  private elementModifierClassName(name: string, mod = "") {
    return `${this.elementClassName(name)}${mod ? `--${mod}` : ""}`
  }
}

export function initWebcamPopup(parent = document.body) {
  if (activePopup) return activePopup

  const root = document.createElement("div")
  root.classList.add(WebcamPopup.cssClass)
  parent.appendChild(root)
  return new WebcamPopup(root, undefined)
}

export function openWebcamPopup(data: WebcamPopupData) {
  activePopup?.open(data)
}
